import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { BusinessCrumb } from './businessCrumbs.js';

export type BusinessCrumbsOverride = {
  crumbLabel?: string;
  breadcrumbs?: BusinessCrumb[];
};

type BusinessCrumbsContextValue = {
  override: BusinessCrumbsOverride;
  publish: (next: BusinessCrumbsOverride) => void;
};

const BusinessCrumbsContext = createContext<BusinessCrumbsContextValue | null>(null);

export function BusinessCrumbsProvider({ children }: { children: ReactNode }) {
  const [override, setOverride] = useState<BusinessCrumbsOverride>({});
  const value = useMemo(
    () => ({
      override,
      publish: setOverride,
    }),
    [override],
  );
  return <BusinessCrumbsContext.Provider value={value}>{children}</BusinessCrumbsContext.Provider>;
}

export function useBusinessCrumbsOverride(): BusinessCrumbsOverride {
  return useContext(BusinessCrumbsContext)?.override ?? {};
}

/** Feature pages may set the leaf label (e.g. department name "Legal") without owning chrome. */
export function usePublishBusinessCrumbs(input: BusinessCrumbsOverride): void {
  const publish = useContext(BusinessCrumbsContext)?.publish;
  const crumbLabel = input.crumbLabel;
  const breadcrumbs = input.breadcrumbs;
  const breadcrumbsKey = breadcrumbs?.map((c) => `${c.label}:${c.href ?? ''}`).join('|');

  useEffect(() => {
    if (!publish) return;
    publish({ crumbLabel, breadcrumbs });
    return () => publish({});
  }, [publish, crumbLabel, breadcrumbs, breadcrumbsKey]);
}
